import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMember } from "@/context/MemberAuthContext";
import { PageShell } from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Mail, ShieldCheck, ArrowRight, CheckCircle2, Info } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLoginUrl = () => {
  const redirectUrl = window.location.origin + "/member/login";
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 43.5c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 34.5 26.7 35.5 24 35.5c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36.3 43.5 30.7 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const MemberLogin = () => {
  const { member, requestOtp, verifyOtp, googleExchange, confirmIdentity } = useMember();
  const navigate = useNavigate();
  const [step, setStep] = useState("choose"); // choose | email | otp | confirm
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const processedSession = useRef(false);

  // OAuth callback: exchange session_id returned by Emergent Google auth
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=") && !processedSession.current) {
      processedSession.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      setBusy(true);
      googleExchange(sid)
        .then((data) => {
          if (data.needs_confirmation) { setMe(data.member); setStep("confirm"); }
          else { toast.success("Welcome back!"); navigate("/member"); }
        })
        .catch((err) => toast.error(err?.response?.data?.detail || "This email is not registered with the association."))
        .finally(() => setBusy(false));
    }
  }, [googleExchange, navigate]);

  useEffect(() => { if (member && step === "choose") navigate("/member"); }, [member, step, navigate]);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email address / માન્ય ઇમેઇલ દાખલ કરો"); return; }
    setBusy(true);
    try {
      const r = await requestOtp(email);
      toast.message(r.message);
      setMaskedEmail(r.masked_email || "");
      setStep("otp");
    } catch { toast.error("Could not send code. Please try again."); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await verifyOtp(email, code);
      setMe(data.member);
      if (data.needs_confirmation) setStep("confirm");
      else { toast.success("Welcome back!"); navigate("/member"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try { await confirmIdentity(); toast.success("Identity confirmed — you're verified."); navigate("/member"); }
    catch { toast.error("Could not confirm"); }
    finally { setBusy(false); }
  };

  return (
    <PageShell>
      <Toaster position="top-center" richColors />
      <div className="gmoaa-container flex justify-center py-16">
        <div className="w-full max-w-md rounded-2xl border border-gmoaa-border-subtle bg-white p-8 shadow-sm">
          <div className="mb-6 text-center">
            <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gmoaa-green-light text-gmoaa-green">
              {step === "confirm" ? <ShieldCheck size={26} /> : <Mail size={26} />}
            </span>
            <h1 className="font-serif text-2xl font-bold text-gmoaa-green">Member Login</h1>
            <p className="font-gujarati text-sm text-gmoaa-text-muted">સભ્ય લોગિન</p>
            <p className="mt-1 text-sm text-gmoaa-text-muted">Sign in with your registered email · તમારા નોંધાયેલા ઇમેઇલથી સાઇન ઇન કરો</p>
          </div>

          {step === "choose" && (
            <div className="space-y-4">
              <Button data-testid="member-google-login" disabled={busy} onClick={() => { window.location.href = googleLoginUrl(); }}
                className="flex w-full items-center justify-center border border-gmoaa-border-strong bg-white text-gmoaa-text-dark hover:bg-gmoaa-surface">
                <GoogleIcon /> {busy ? "Signing in…" : "Continue with Google"}
              </Button>
              <div className="flex items-center gap-3 text-xs text-gmoaa-text-muted">
                <span className="h-px flex-1 bg-gmoaa-border-subtle" /> or / અથવા <span className="h-px flex-1 bg-gmoaa-border-subtle" />
              </div>
              <Button data-testid="member-emailcode-option" variant="outline" disabled={busy} onClick={() => setStep("email")}
                className="w-full border-gmoaa-green text-gmoaa-green hover:bg-gmoaa-green-light">
                <Mail size={16} className="mr-2" /> Login with email code · ઇમેઇલ કોડથી લોગિન
              </Button>
              <div className="flex items-start gap-2 rounded-lg bg-gmoaa-saffron-light px-3 py-2 text-xs text-gmoaa-saffron-hover">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>If Google does not open inside WhatsApp or Instagram, open this page in Chrome. · જો WhatsApp કે Instagram માં Google ન ખૂલે, તો આ પેજ Chrome માં ખોલો.</span>
              </div>
              <p className="text-center text-sm text-gmoaa-text-muted">
                Not on the list? · યાદીમાં નથી? <Link to="/request-access" className="font-medium text-gmoaa-saffron-hover hover:underline">Request access</Link>
              </p>
            </div>
          )}

          {step === "email" && (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <Label className="mb-1.5 block text-sm">Registered email · નોંધાયેલ ઇમેઇલ</Label>
                <Input data-testid="member-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="border-gmoaa-border-subtle" />
              </div>
              <Button data-testid="member-send-code" type="submit" disabled={busy} className="w-full bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
                {busy ? "Sending…" : "Send code · કોડ મોકલો"} <ArrowRight size={16} className="ml-1" />
              </Button>
              <button type="button" onClick={() => setStep("choose")} className="w-full text-center text-sm text-gmoaa-text-muted hover:underline">Back · પાછા</button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verify} className="space-y-4">
              <p className="rounded-lg bg-gmoaa-green-light px-3 py-2 text-sm text-gmoaa-green">
                {maskedEmail
                  ? <>A one-time code was sent to <b>{maskedEmail}</b>. Enter it below (valid 10 minutes).</>
                  : <>If this email is registered, a 6-digit code has been sent. Enter it below (valid 10 minutes).</>}
              </p>
              <div>
                <Label className="mb-1.5 block text-sm">6-digit code · ૬-અંકનો કોડ</Label>
                <Input data-testid="member-otp" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="------" className="border-gmoaa-border-subtle text-center text-lg tracking-[0.4em]" />
              </div>
              <Button data-testid="member-verify-otp" type="submit" disabled={busy} className="w-full bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
                {busy ? "Verifying…" : "Verify & sign in · ચકાસો અને સાઇન ઇન"}
              </Button>
              <button type="button" onClick={() => setStep("email")} className="w-full text-center text-sm text-gmoaa-text-muted hover:underline">Change email · ઇમેઇલ બદલો</button>
            </form>
          )}

          {step === "confirm" && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-gmoaa-text-muted">Is this you? Please confirm your details to get your verified green status.</p>
              <div className="rounded-xl border border-gmoaa-border-subtle bg-gmoaa-surface p-4 text-left">
                <p className="font-serif text-lg font-semibold text-gmoaa-text-dark">{me?.name_en}</p>
                <p className="font-gujarati text-sm text-gmoaa-text-muted">{me?.name_gu}</p>
                <p className="mt-1 text-xs uppercase tracking-wide text-gmoaa-saffron-hover">{me?.district_slug}</p>
              </div>
              <Button data-testid="member-confirm" onClick={doConfirm} disabled={busy} className="w-full bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
                <CheckCircle2 size={16} className="mr-1.5" /> Yes, this is me · હા, આ હું જ છું
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default MemberLogin;
